import React, { useState, useEffect } from 'react';
import AppLayout from '../../components/AppLayout';
import { useTheme } from '../../contexts/ThemeContext';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Mail, Phone, MapPin, Briefcase, Building, Tag, Clock } from 'lucide-react';
import { STAGE_MAP } from '../../components/prospecting/constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeadDetail() {
  const { isDark } = useTheme();
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLead(); }, [leadId]);

  const fetchLead = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospecting/leads/${leadId}`, { credentials: 'include' });
      if (res.ok) setLead(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleStageChange = async (newStage) => {
    await fetch(`${BACKEND_URL}/api/prospecting/leads/${leadId}/stage`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ stage: newStage }),
    });
    fetchLead();
  };

  const card = `${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border rounded-xl`;

  if (loading) {
    return (
      <AppLayout title="Lead Detail">
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#A2FF00]"></div></div>
      </AppLayout>
    );
  }

  if (!lead) {
    return (
      <AppLayout title="Lead Detail">
        <div className="text-center py-12">
          <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>Lead not found</p>
          <button onClick={() => navigate('/lead-finder/pipeline')} className="mt-4 text-[#A2FF00] hover:underline">Back to Pipeline</button>
        </div>
      </AppLayout>
    );
  }

  const stage = STAGE_MAP[lead.stage] || { label: lead.stage, color: 'bg-gray-500' };
  const li = lead.linkedin || {};
  const ct = lead.contact || {};
  const sc = lead.scoring || {};

  return (
    <AppLayout title="Lead Detail">
      <div className="space-y-6" data-testid="lead-detail">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/lead-finder/pipeline')} className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`} data-testid="back-to-pipeline">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {li.first_name || ''} {li.last_name || 'Unknown'}
            </h1>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {li.current_title || ''}{li.current_title && li.current_company ? ' at ' : ''}{li.current_company || ''}
            </p>
          </div>
          <select
            value={lead.stage}
            onChange={e => handleStageChange(e.target.value)}
            className={`px-3 py-2 rounded-lg text-sm font-medium ${stage.color} text-white border-none`}
            data-testid="lead-stage-select"
          >
            {Object.entries(STAGE_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Info */}
          <div className={`lg:col-span-2 ${card} p-5 space-y-5`}>
            <h2 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Profile Information</h2>

            {li.headline && (
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{li.headline}</p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {ct.email && (
                <div className="flex items-center gap-2">
                  <Mail className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{ct.email}</span>
                </div>
              )}
              {ct.phone && (
                <div className="flex items-center gap-2">
                  <Phone className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{ct.phone}</span>
                </div>
              )}
              {li.location && (
                <div className="flex items-center gap-2">
                  <MapPin className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{li.location}</span>
                </div>
              )}
              {li.industry && (
                <div className="flex items-center gap-2">
                  <Building className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{li.industry}</span>
                </div>
              )}
              {li.profile_url && (
                <div className="flex items-center gap-2">
                  <ExternalLink className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                  <a href={li.profile_url} target="_blank" rel="noreferrer" className="text-sm text-blue-400 hover:underline">LinkedIn Profile</a>
                </div>
              )}
            </div>

            {li.summary && (
              <div>
                <h3 className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Summary</h3>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{li.summary}</p>
              </div>
            )}

            {lead.tags?.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                {lead.tags.map((tag, i) => (
                  <span key={`tag-${tag}`} className={`px-2 py-0.5 rounded text-xs ${isDark ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>{tag}</span>
                ))}
              </div>
            )}
          </div>

          {/* Scoring & Activity */}
          <div className="space-y-4">
            {/* Score Card */}
            <div className={`${card} p-5`}>
              <h2 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>ICP Score</h2>
              {sc.score != null ? (
                <div className="text-center">
                  <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full border-4 ${sc.score >= 70 ? 'border-green-500' : sc.score >= 40 ? 'border-yellow-500' : 'border-red-400'}`}>
                    <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{sc.score}</span>
                  </div>
                  {sc.confidence && <p className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Confidence: {sc.confidence}</p>}
                  {sc.reasoning && <p className={`mt-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{sc.reasoning}</p>}
                  {sc.talking_points?.length > 0 && (
                    <div className="mt-3 text-left">
                      <p className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Talking Points</p>
                      <ul className="space-y-1">
                        {sc.talking_points.map((p, i) => (
                          <li key={`tp-${i}-${p.slice(0,10)}`} className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>- {p}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <p className={`text-sm text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Not scored yet</p>
              )}
            </div>

            {/* Activity Timeline */}
            <div className={`${card} p-5`}>
              <h2 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Activity</h2>
              {lead.activities?.length > 0 ? (
                <div className="space-y-3">
                  {lead.activities.slice().reverse().map((a, i) => (
                    <div key={`act-${a.timestamp}-${i}`} className="flex gap-3">
                      <Clock className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                      <div>
                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{a.details}</p>
                        <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No activity yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
